import React, { useContext } from "react";
import { sendPageView } from "js/utils/google-analytics";
import { PHOTO_CHECKOUT, PHOTO_DETAIL } from "data/links";
import { PhotosContext } from "photos/context";

export const handleGaEvents = ({
    photoId,
    licenseName = "",
    selectLicense = false, // step 2
    checkout = false, // step 3
    download = false // step 4
}) => {
    if (selectLicense) {
        return sendPageView(`/checkout/photo/select-license/${photoId}/`);
    }

    if (checkout) {
        licenseName = String(licenseName).toLowerCase();
        return sendPageView(
            `/checkout/photo/payment/${licenseName}/${photoId}/`
        );
    }

    if (download) {
        let displayName = "";
        switch (licenseName) {
            case KEY_PUBLIC_RES:
                displayName = "PD";
                break;
            case KEY_BASIC_RES:
                displayName = "Attribution";
                break;
            case KEY_LARGE_RES:
                displayName = "Large";
                break;
            case KEY_FULL_RES:
                displayName = "FullRes";
                break;
        }
        return sendPageView(`/download/photo/${displayName}/${photoId}/`);
    }
};

// constants
export const NAME_LARGE_RES = "Large Download";
export const NAME_FULL_RES = "Full Res Download";
export const NAME_BASIC_RES = "Basic Download";

export const KEY_LARGE_RES = "LARGE";
export const KEY_FULL_RES = "FULL_RES";
export const KEY_BASIC_RES = "BASIC";
export const KEY_PUBLIC_RES = "PUBLIC";

export const QUERY_OPTIONS = "options";
export const QUERY_PURCHASED = "purchase";

export const getBackendKey = name => {
    switch (name) {
        case NAME_LARGE_RES:
            return KEY_LARGE_RES;
        case NAME_FULL_RES:
            return KEY_FULL_RES;
        case NAME_BASIC_RES:
            return KEY_BASIC_RES;
        default:
            return null;
    }
};

export const getValidKey = key => {
    key = String(key).toLowerCase();
    switch (key) {
        case KEY_LARGE_RES.toLowerCase():
            return KEY_LARGE_RES;
        case KEY_FULL_RES.toLowerCase():
            return KEY_FULL_RES;
        case KEY_BASIC_RES.toLowerCase():
            return KEY_BASIC_RES;
        default:
            return null;
    }
};

export const getPrice = nameOrKey => {
    switch (nameOrKey) {
        case KEY_LARGE_RES:
        case NAME_LARGE_RES:
            return "8.50";
        case KEY_FULL_RES:
        case NAME_FULL_RES:
            return "33";
        case KEY_BASIC_RES:
        case NAME_BASIC_RES:
            return "0";
    }
    return null;
};

export const getLicenseSchema = (name, { photo, selected, setSelected }) => {
    const dimensions = getDimensionProps(name, photo);
    const key = getBackendKey(name);
    const safeName = key.toLowerCase().replace(/[^a-z]/g, "");
    const mp = getMegapixels(dimensions);

    return {
        id: `radio-${safeName}`,
        backendKey: key,
        title: name,
        ariaLabel: name,
        selected: key === selected,
        price: "$" + getPrice(name),
        optionName: mp,
        onClick: () => setSelected(key),
        attributionDisclaimer: getAttributionDisclaimer(name),
        meta: {
            mp,
            pixelDimensions: getPixelDimensions(dimensions),
            printDimensions: getPrintDimensions(dimensions),
            dpi: getDpi(dimensions)
        },
        link: (
            <>
                {name === NAME_BASIC_RES &&
                    `Must attribute artist, no edits per `}
                <a href="/legal/terms-of-use/#photo-licenses" target="_blank">
                    {getLinkCopy(name)}
                </a>
            </>
        )
    };
};

const getDimensionProps = (name, photo) => {
    switch (name) {
        case NAME_LARGE_RES:
            return photo.largeDimensions;
        case NAME_FULL_RES:
            return photo.fullDimensions;
        case NAME_BASIC_RES:
            return photo.freeDimensions;
    }
};

const getToggleName = name => {
    switch (name) {
        case NAME_LARGE_RES:
            return "5 MP";
        case NAME_FULL_RES:
            return "10+ MP";
    }
};

const getLinkCopy = name => {
    switch (name) {
        case NAME_LARGE_RES:
            return "Royalty-Free Noncommercial License";
        case NAME_FULL_RES:
            return "Royalty-Free Limited Commercial License";
        case NAME_BASIC_RES:
            return "license";
    }
};

const getAttributionDisclaimer = name => {
    switch (name) {
        case NAME_BASIC_RES:
            return "Must attribute artist. No edits.";
        case NAME_LARGE_RES:
        case NAME_FULL_RES:
            return "Purchase supports the artist";
    }
};

const getMegapixels = dimensions => {
    if (dimensions && dimensions.pixelWidth && dimensions.pixelHeight) {
        const raw = (dimensions.pixelWidth * dimensions.pixelHeight) / 1000000;
        const mp =
            raw < 1
                ? `.${Math.round(raw * 10)}`
                : raw > 9
                ? "10+"
                : Math.round(raw); // .2 MP, 5 MP, 10+ MP
        return `${mp} MP`;
    }
    return null;
};

const getPixelDimensions = dimensions => {
    if (dimensions && dimensions.pixelWidth && dimensions.pixelHeight) {
        return `${dimensions.pixelWidth}x${dimensions.pixelHeight} px`;
    }
    return null;
};

const getPrintDimensions = dimensions => {
    if (dimensions && dimensions.printWidth && dimensions.printHeight) {
        return `${dimensions.printWidth}"x${dimensions.printHeight}"`;
    }
    return null;
};

const getDpi = dimensions => {
    if (dimensions && dimensions.printDpi) {
        return `${dimensions.printDpi} dpi`;
    }
    return null;
};

export const getFormattedData = ({
    slugWithId,
    response,
    selectedLicense,
    photo,
    photoId
}) => {
    const { history } = useContext(PhotosContext);

    const photoDetailPagePath = PHOTO_DETAIL.replace(
        ":photoSlugWithId",
        slugWithId
    );

    const initialForm = {
        name: "",
        address_zip: ""
    };

    // used in OrderPreview to show which photo res (Large or Full) download they are purchasing
    const selectedPrice = getPrice(selectedLicense);
    const setLicense = licenseName => {
        history.replace(
            PHOTO_CHECKOUT.replace(":purchaseType", licenseName).replace(
                ":photoId",
                photoId
            )
        );
    };
    const schemaProps = {
        photo,
        selected: selectedLicense,
        setSelected: setLicense
    };

    // used in OrderPreview to show which photo res (Large or Full) download they are purchasing
    const licenseSchemas = [
        getLicenseSchema(NAME_LARGE_RES, schemaProps),
        getLicenseSchema(NAME_FULL_RES, schemaProps)
    ];

    return {
        initialForm,
        photoDetailPagePath,
        hasSavedCard:
            response && Object.entries(response).length >= 1 ? true : false,
        licenseSchemas,
        selectedPrice
    };
};
